// How every library screen names a person (IA-1, U7). A person never reads an id: not the user's
// uuid, and not an IdP's opaque external id. For an OIDC, Cognito or trusted-token person that is
// `<issuer hash>:<sub>`, and a sub is often a uuid. So the name is the display name, else the
// email, else, for a person who signs in with AWS, the name their AWS principal carries, else
// these words. The one copy; the screens re-export it under their own barrels.

export const UNKNOWN_USER = "Unknown user";

export interface PersonNameParts {
  displayName?: string | null | undefined;
  email?: string | null | undefined;
  /** Read only for an AWS principal's name (`awsPrincipalName`); never shown as it is. */
  externalId?: string | null | undefined;
}

/** The display name, else the email, else an AWS principal's name, else "Unknown user". */
export function personName({ displayName, email, externalId }: PersonNameParts): string {
  return displayName?.trim() || email?.trim() || awsPrincipalName(externalId) || UNKNOWN_USER;
}

/** Anything uuid-shaped, anywhere in the text (IA-1's scan; some SSO session names are one). */
const UUID_SHAPED = /[0-9a-f]{8}-[0-9a-f]{4}-/i;

/** A name worth showing: non-empty and not uuid-shaped. */
function usable(part: string | undefined): string | null {
  const text = part?.trim() ?? "";
  return text !== "" && !UUID_SHAPED.test(text) ? text : null;
}

/** `aws:<account>:<resource>`: the external id the proxy meters an AWS caller as (identity/aws.py). */
const NEXUS_AWS_ID = /^aws:\d{12}:(.+)$/;
/** `arn:<partition>:<service>:<region>:<account>:<resource>`. */
const ARN = /^arn:[^:]+:([^:]*):[^:]*:[^:]*:(.+)$/;

/**
 * The name an AWS principal carries, from its external id, or null for any other form:
 * - `…assumed-role/<role>/<session>` → `<session> (<role>)`; a uuid-shaped session → `<role>`;
 * - `…user/<path>/<name>`, `…federated-user/<name>` → `<name>`;
 * - any other `arn:` → its last path segment.
 * A part that is uuid-shaped is never used; with nothing left, null.
 */
export function awsPrincipalName(externalId: string | null | undefined): string | null {
  const id = externalId?.trim() ?? "";
  const nexus = NEXUS_AWS_ID.exec(id);
  const arn = nexus ? null : ARN.exec(id);
  const resource = nexus?.[1] ?? arn?.[2];
  if (resource === undefined) return null;
  const parts = resource.split("/");
  const kind = parts[0];
  if (kind === "assumed-role" && parts.length >= 3) {
    const role = usable(parts[1]);
    const session = usable(parts[parts.length - 1]);
    if (session && role) return `${session} (${role})`;
    return session ?? role;
  }
  if ((kind === "user" || kind === "federated-user") && parts.length >= 2) return usable(parts[parts.length - 1]);
  // Any other ARN by its last path segment; the proxy's own form knows no other kind (root: none).
  if (!arn) return null;
  const last = parts.length > 1 ? parts[parts.length - 1] : resource.split(":").pop();
  return usable(last);
}
